import { useQuery } from '@tanstack/react-query';
import { Inbox, Search, UserRound } from 'lucide-react';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { Badge, Button, Card, CardHeader, EmptyState, Skeleton, inputCls } from '../../components/ui';
import { reportClientError } from '../../lib/errors';
import { localToday, ruDate, ruDateTime, weekday } from '../../lib/format';
import { useRole } from '../../lib/role';
import { type SampleLogEntry, type SamplesLogResponse, samplesLog } from './api';
import { describeCondition, useConditionNames } from './condition';
import { Fact, LoadError, SCREEN, shouldReport } from './parts';
import { CONDITION_LABEL, LABEL, T, TITLE, say } from './texts';

/*
 * Журнал выборок (005, T139; FR-026; contracts/http-api.md, `GET /api/admin/agent-data/samples?from&to&personId?`):
 * кто, каким клиентом, по какому условию и сколько дней получил агент; отметка «меньше 5 человек». Ответ —
 * `SamplesLogResponse`; условие словами — `condition.ts` (названия правил — со страницы «Правила расчёта»). Людей в
 * списке выборок нет: человеко-дни (`items`) приходят, но показываются только при поиске по человеку. Поиск по номеру
 * СКУД отвечает на вопрос «чьи данные ушли и когда»: какие дни человека ушли агентам, в какой выборке, кто и каким
 * клиентом их получил. Это право администратора системы (таблица прав 005).
 * - Период по умолчанию — 30 дней по сегодня (дата объекта). Фильтр применяется кнопкой «Показать» или Enter.
 * - Сервер отдаёт не больше 500 выборок, новые сверху; больше (`truncated`) — над списком: сузьте период.
 * - Один интерфейс на 375/768/1280 px: на компьютере фильтр — в одну строку, до 900 px — даты в две колонки, номер и
 *   кнопка — во всю ширину; сведения выборки — в три колонки, на телефоне — столбцом.
 */

/** Дата `YYYY-MM-DD` со сдвигом на `n` дней. */
function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

interface Filter {
  from: string;
  to: string;
  person: string;
}

export function SamplesLog({ tz }: { tz: string }) {
  const ids = useId();
  const { role } = useRole();
  const [initial] = useState<Filter>(() => {
    const today = localToday(tz);
    return { from: addDays(today, -29), to: today, person: '' };
  });
  const [draft, setDraft] = useState<Filter>(initial);
  const [applied, setApplied] = useState<Filter>(initial);
  const [periodError, setPeriodError] = useState(false);
  const q = useQuery({
    queryKey: ['agent-data-samples', role, applied.from, applied.to, applied.person],
    queryFn: () => samplesLog({ from: applied.from, to: applied.to, personId: applied.person || undefined }),
  });

  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'load samples', q.error);
  }, [q.error]);

  const apply = (e: FormEvent) => {
    e.preventDefault();
    const bad = !DATE.test(draft.from) || !DATE.test(draft.to) || draft.from > draft.to;
    setPeriodError(bad);
    if (bad) return;
    const next = { ...draft, person: draft.person.trim() };
    setDraft(next);
    if (next.from === applied.from && next.to === applied.to && next.person === applied.person)
      void q.refetch();
    else setApplied(next);
  };

  const allSamples = () => {
    setDraft((d) => ({ ...d, person: '' }));
    setApplied((a) => ({ ...a, person: '' }));
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title={TITLE.log} description={T.logIntro} />
        <form
          role="search"
          aria-label={TITLE.log}
          noValidate
          onSubmit={apply}
          className="grid grid-cols-[minmax(0,170px)_minmax(0,170px)_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-3 border-t border-line px-6 py-4 max-tablet:grid-cols-2 max-sm:px-4"
        >
          <DateField
            id={`${ids}-from`}
            label={LABEL.from}
            value={draft.from}
            invalid={periodError}
            onChange={(v) => setDraft((d) => ({ ...d, from: v }))}
          />
          <DateField
            id={`${ids}-to`}
            label={LABEL.to}
            value={draft.to}
            invalid={periodError}
            onChange={(v) => setDraft((d) => ({ ...d, to: v }))}
          />
          <div className="min-w-0 max-tablet:col-span-2">
            <label htmlFor={`${ids}-person`} className="mb-1.5 block text-[13px] font-medium text-ink">
              {LABEL.person}
            </label>
            <input
              id={`${ids}-person`}
              value={draft.person}
              maxLength={64}
              autoComplete="off"
              aria-describedby={`${ids}-person-hint`}
              onChange={(e) => setDraft((d) => ({ ...d, person: e.target.value }))}
              className={inputCls}
            />
            <p id={`${ids}-person-hint`} className="mt-1 text-[12px] text-ink-3">
              {T.personHint}
            </p>
          </div>
          <div className="pt-[26px] max-tablet:col-span-2 max-tablet:pt-0 max-sm:[&>button]:w-full">
            <Button type="submit" variant="primary" icon={Search}>
              {LABEL.show}
            </Button>
          </div>
          {periodError && (
            <p role="alert" className="col-span-full -mt-1 text-[12.5px] text-bad">
              {T.periodError}
            </p>
          )}
        </form>
      </Card>

      {q.isError ? (
        <LoadError
          title={TITLE.logError}
          error={q.error}
          busy={q.isFetching}
          onRetry={() => void q.refetch()}
        />
      ) : !q.data ? (
        <Loading />
      ) : (
        <>
          {q.data.truncated && (
            <p role="note" className="text-[13px] text-ink-2">
              {T.truncated}
            </p>
          )}
          {applied.person ? (
            <PersonDays log={q.data} personId={applied.person} tz={tz} onAll={allSamples} />
          ) : (
            <Samples samples={q.data.samples} tz={tz} />
          )}
        </>
      )}
    </div>
  );
}

function DateField({
  id,
  label,
  value,
  invalid,
  onChange,
}: { id: string; label: string; value: string; invalid: boolean; onChange: (v: string) => void }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        aria-invalid={invalid ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} aria-invalid:border-bad tabular-nums`}
      />
    </div>
  );
}

/** Клиент выборки: имя из белого списка; выборка из интерфейса без клиента — «Интерфейс табеля». */
const clientOf = (s: SampleLogEntry) => s.client?.name ?? LABEL.fromUi;
/** Автор выборки: имени нет в базе — прочерк. */
const userOf = (s: SampleLogEntry) => s.user.name || '—';

/** Выборки без людей: человеко-дни (`items`) в этом списке не показываются. */
function Samples({ samples, tz }: { samples: readonly SampleLogEntry[]; tz: string }) {
  const names = useConditionNames(samples);
  if (samples.length === 0)
    return (
      <Card>
        <EmptyState icon={Inbox} title={TITLE.logEmpty} description={T.logEmpty} />
      </Card>
    );
  return (
    <Card>
      <ol aria-label={LABEL.samples} className="divide-y divide-line">
        {samples.map((s) => (
          <li key={s.id} className="px-6 py-4 max-sm:px-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[14px] font-semibold text-ink">{say.sample(s.id)}</span>
              <span className="text-[13px] text-ink-2 tabular-nums">{ruDateTime(s.createdAt, tz)}</span>
              {s.smallGroup && <Badge tone="warn">{LABEL.smallGroup}</Badge>}
            </div>
            <p className="mt-1 text-[13.5px] break-words text-ink">
              <span className="sr-only">{LABEL.condition}: </span>
              {describeCondition(s, names)}
            </p>
            <dl className="mt-2 grid grid-cols-3 gap-x-6 gap-y-2 max-sm:grid-cols-1">
              <Fact label={LABEL.who}>{userOf(s)}</Fact>
              <Fact label={LABEL.client}>{clientOf(s)}</Fact>
              <Fact label={LABEL.days}>
                <span className="tabular-nums">{say.days(s.days)}</span>
              </Fact>
            </dl>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/**
 * Дни человека, которые ушли агентам: дата дня, выборка, когда, кто, клиент. Сервер с `personId` присылает только
 * выборки с днями этого человека и только его дни (`items`); человек — номером СКУД, ФИО в ответе нет.
 */
function PersonDays({
  log,
  personId,
  tz,
  onAll,
}: { log: SamplesLogResponse; personId: string; tz: string; onAll: () => void }) {
  const headingId = useId();
  const days = log.samples
    .flatMap((s) => s.items.filter((i) => i.personId === personId).map((i) => ({ date: i.date, s })))
    .sort((a, b) => a.date.localeCompare(b.date) || a.s.id - b.s.id);
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-5 pb-4 max-sm:px-4">
        <h3 id={headingId} className="flex min-w-0 items-center gap-2 text-[15px] font-semibold text-ink">
          <UserRound size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
          <span className="break-words">{say.person(personId)}</span>
        </h3>
        <Button variant="ghost" onClick={onAll} className="max-sm:w-full">
          {LABEL.allSamples}
        </Button>
      </div>
      {days.length === 0 ? (
        <p className="border-t border-line px-6 py-5 text-[13.5px] text-ink-2 max-sm:px-4">{T.personEmpty}</p>
      ) : (
        <ol aria-label={LABEL.personDays} className="divide-y divide-line border-t border-line">
          {days.map(({ date, s }) => (
            <li key={`${s.id}-${date}`} className="px-6 py-3 max-sm:px-4">
              <p className="text-[14px] font-semibold text-ink tabular-nums">{`${weekday(date)}, ${ruDate(date)}`}</p>
              <dl className="mt-1.5 grid grid-cols-4 gap-x-6 gap-y-2 max-tablet:grid-cols-2 max-sm:grid-cols-1">
                <Fact label={LABEL.sampleNo}>{say.sample(s.id)}</Fact>
                <Fact label={LABEL.when}>
                  <span className="tabular-nums">{ruDateTime(s.createdAt, tz)}</span>
                </Fact>
                <Fact label={LABEL.who}>{userOf(s)}</Fact>
                <Fact label={LABEL.client}>{clientOf(s)}</Fact>
              </dl>
              {s.source === 'day_link' && (
                <p className="mt-1 text-[12.5px] text-ink-3">{CONDITION_LABEL.dayLink}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function Loading() {
  return (
    <div aria-busy="true" aria-label={LABEL.loading}>
      <Card className="divide-y divide-line">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-2 px-6 py-4 max-sm:px-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-3/5" />
            <Skeleton className="h-3 w-2/5" />
          </div>
        ))}
      </Card>
    </div>
  );
}
